import { z } from 'zod';
import { zTimestamptz, zUuid } from './primitives';

/** Local sync bookkeeping (docs/08 §7, §8c). Present from Task 3 (ADR-016). */
export const localSyncFields = {
  _localDirty: z.boolean(),
  _baseServerUpdatedAt: zTimestamptz.nullable(),
};

/**
 * Build the three shapes for a synced entity (docs/08 §8c).
 * `fields` must NOT include user_id / created_at / updated_at / deleted_at.
 * Return type is inferred so Local/Remote `z.infer` keeps per-field types
 * (an explicit ZodRawShape return wiped field types to `any`).
 */
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types -- infer generic entity shapes
export function defineSyncedEntity<T extends z.ZodRawShape>(fields: T) {
  const body = z.object(fields);

  const remoteRow = body.extend({
    user_id: zUuid,
    created_at: zTimestamptz,
    updated_at: zTimestamptz,
    deleted_at: zTimestamptz.nullable(),
  });

  const localEntity = body.extend({
    user_id: zUuid.nullable(),
    created_at: zTimestamptz,
    updated_at: zTimestamptz,
    deleted_at: zTimestamptz.nullable(),
    ...localSyncFields,
  });

  const remoteInsertPayload = body.extend({
    user_id: zUuid,
    deleted_at: zTimestamptz.nullable(),
  });

  return { body, remoteRow, localEntity, remoteInsertPayload };
}

export type LocalEntityOf<S extends z.ZodTypeAny> = z.infer<S>;
